import React from "react";
import { DeleteOutlined, FileSyncOutlined, QuestionCircleTwoTone, UndoOutlined } from "@ant-design/icons";
import { Table, Popconfirm, Button, notification, Space, Checkbox, Modal, Typography } from "antd";
import { Common } from "@secondcloset/types";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";
import { TableProps } from "antd/es/table";
import { updateUser } from "../../../../../api/accounts/user";

type User = Common.User;
interface UserTableProps extends TableProps<User> {
  page: number;
  setPage?: (page: number) => unknown;
  pageSize: number;
  isLoading: boolean;
  data: User[];
  organizationID: string;
  onReFetchUsers: () => Promise<User[] | unknown>;
  showActive: boolean;
  // Batch selection props
  selectedUsers?: React.Key[];
  setSelectedUsers: (users: React.Key[]) => void;
  areAllUsersSelected?: boolean;
  setAreAllUsersSelected: (selected: boolean) => void;
  excludedUsers?: React.Key[];
  setExcludedUsers: (users: React.Key[]) => void;
  currentPageUsers?: React.Key[];
  isBatchActionMode?: boolean;
}
const UsersTable: React.FC<UserTableProps> = ({
  page,
  setPage,
  pageSize,
  isLoading,
  data,
  organizationID,
  onReFetchUsers,
  showActive,
  // Batch selection props
  selectedUsers = [],
  setSelectedUsers,
  areAllUsersSelected = false,
  setAreAllUsersSelected,
  excludedUsers = [],
  setExcludedUsers,
  currentPageUsers = [],
  isBatchActionMode = false,
  ...tableProps
}) => {
  const { mutate: doUpdateUser, isLoading: isUpdating } = useMutation(updateUser, {
    onSuccess: (user: User) => {
      notification.success({
        message: `${user.email} updated successfully`,
      });
      onReFetchUsers();
    },
  });

  const regeneratePassword = (user_id: string) => {
    const password = Math.random().toString(36).slice(2, 8);
    doUpdateUser(
      {
        userID: user_id,
        body: { user: { password: password } },
      },
      {
        onSuccess: (user) => {
          Modal.info({
            title: "Account Credentials",
            content: (
              <Space direction="vertical">
                <Typography.Text>
                  User:&nbsp;
                  <Typography.Text strong copyable>
                    {user.email}
                  </Typography.Text>
                </Typography.Text>
                <Typography.Text>
                  Password:&nbsp;
                  <Typography.Text strong copyable>
                    {password}
                  </Typography.Text>
                </Typography.Text>
              </Space>
            ),
          });
          return;
        },
      }
    );
  };

  const headerCheckbox = (
    <Space>
      <Checkbox
        checked={areAllUsersSelected}
        onChange={() => {
          if (!areAllUsersSelected) {
            // When checking "All", select current page users and set areAllUsersSelected
            setSelectedUsers([...currentPageUsers]);
            setAreAllUsersSelected(true);
          } else {
            // When unchecking "All", clear selections
            setSelectedUsers([]);
            setAreAllUsersSelected(false);
            setExcludedUsers([]);
          }
        }}
      />
    </Space>
  );

  const renderUserName = (_: string, user: User) => {
    const { id, first_name = "", last_name = "" } = user;
    return (
      <Link to={`/organizations/${organizationID}/users/${id}/edit`}>
        {first_name} {last_name}
      </Link>
    );
  };

  const renderAction = (_: string, user: User) => {
    return (
      <Space>
        <Popconfirm
          title={`Are you sure you want to ${showActive ? "disable" : "enable"} ${user.first_name} ${user.last_name}?`}
          icon={<QuestionCircleTwoTone twoToneColor="red" />}
          onConfirm={() => {
            doUpdateUser({ userID: user.id, body: { user: { disabled: showActive } } });
          }}
          okButtonProps={{ loading: isUpdating }}
          okText="Yes"
          cancelText="No"
        >
          <Button
            icon={showActive ? <DeleteOutlined /> : <UndoOutlined />}
            title={showActive ? "Disable User" : "Enable User"}
            type="text"
            size="small"
            danger
          />
        </Popconfirm>
        {showActive && (
          <Popconfirm
            title={`Are you sure you want to generate new password for ${user.first_name} ${user.last_name}?`}
            icon={<QuestionCircleTwoTone twoToneColor="red" />}
            onConfirm={() => regeneratePassword(user.id)}
            okButtonProps={{ loading: isUpdating }}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<FileSyncOutlined />} title="Regenerate Password" type="text" size="small" />
          </Popconfirm>
        )}
      </Space>
    );
  };
  const renderDefault = (text: string) => text || "-";

  const rowSelection = {
    columnTitle: headerCheckbox,
    columnWidth: "50px",
    hideSelectAll: true,
    selectedRowKeys: selectedUsers.filter((userId) => !excludedUsers.includes(userId)),
    onSelect: (record: User, selected: boolean) => {
      let newSelectedUsers: React.Key[];
      let newExcludedUsers: React.Key[] = [...excludedUsers];

      if (selected) {
        newSelectedUsers = [...selectedUsers, record.id];
      } else {
        newSelectedUsers = selectedUsers.filter((userId) => userId !== record.id);
      }

      setSelectedUsers(newSelectedUsers);

      if (areAllUsersSelected) {
        if (!selected) {
          newExcludedUsers = [...excludedUsers, record.id];
          setExcludedUsers(newExcludedUsers);
        } else {
          newExcludedUsers = excludedUsers.filter((id) => id !== record.id);
          setExcludedUsers(newExcludedUsers);
        }

        // Reevaluate areAllUsersSelected based on current selections
        const totalUsers = data.length;
        const effectivelySelectedCount = totalUsers - newExcludedUsers.length;

        if (effectivelySelectedCount < totalUsers) {
          setAreAllUsersSelected(false);
        }
      } else {
        // Check if all rows are now individually selected
        const totalUsers = data.length;
        const uniqueSelectedUsers = new Set(newSelectedUsers);

        if (uniqueSelectedUsers.size === totalUsers) {
          setAreAllUsersSelected(true);
          setExcludedUsers([]); // Clear excluded users when all are selected
        }
      }
    },
    renderCell: (value: boolean, record: User, index: number, originNode: React.ReactNode) => {
      return originNode; // Always show checkboxes now
    },
  };

  return (
    <>
      <Table
        dataSource={data || []}
        columns={[
          {
            title: "User Name",
            dataIndex: "name",
            key: "name",
            render: renderUserName,
          },
          {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: renderDefault,
          },
          {
            title: "Role",
            dataIndex: "organization_role",
            key: "organization_role",
            render: renderDefault,
          },
          {
            title: "Employee ID",
            dataIndex: "position_id",
            key: "position_id",
            render: renderDefault,
          },
          {
            title: "Actions",
            dataIndex: "action",
            key: "action",
            render: renderAction,
          },
        ]}
        size="small"
        scroll={{ x: 800 }}
        rowKey={(record) => record.id}
        loading={isLoading || isUpdating}
        rowSelection={isBatchActionMode ? rowSelection : undefined}
        pagination={{
          current: page,
          total: data?.length < pageSize ? pageSize * page - (pageSize - data?.length) + 1 : pageSize * page + 1,
          onChange: setPage,
          pageSize,
        }}
        {...tableProps}
      />
    </>
  );
};

export default UsersTable;
